import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Section } from './Section';
import { trackAddToCart, trackInitiateCheckout } from '../utils/metaPixel';
import { TimerIcon, CreditCardIcon, SmartphoneIcon, MailIcon, CheckIcon, SparklesIcon, GiftIcon } from 'lucide-react';
const BASE_PRICE = 17390;
const orderBumps = [{
  id: 'sin-azucar',
  title: '30 Recetas Sin Azúcar Ni Gluten',
  desc: 'Un recetario extra con 30 preparaciones libres de azúcar y gluten, ideales para reducir la inflamación y cuidar tu tiroides.',
  price: 12990,
  originalPrice: 24990,
  image: 'https://cdn.shopify.com/s/files/1/0829/1431/2411/files/ORDER_BUMP_RECETA_SIN_AZUCAR_Y_SIN_GLUTEN.png?v=1779798823'
}];
const includedBonuses = [{
  id: 'bono-suplementos',
  label: 'Bono 1',
  title: 'Guía de Suplementación Inteligente',
  desc: 'Qué vitaminas y minerales realmente apoyan tu tiroides (y cuáles son pérdida de dinero).',
  value: 14990,
  image: 'https://cdn.shopify.com/s/files/1/0829/1431/2411/files/BONO1_Guia_de_suplementacion.png?v=1779798823'
}, {
  id: 'bono-planificador',
  label: 'Bono 2',
  title: 'Planificador Semanal Imprimible',
  desc: 'Organiza tus comidas en 5 minutos. Plantillas listas para imprimir y pegar en tu heladera.',
  value: 9990,
  image: 'https://cdn.shopify.com/s/files/1/0829/1431/2411/files/BONO2_PLANIFICADOR_IMPRIMIBLE.png?v=1779798823'
}];
const formatARS = (n: number) => `$${n.toLocaleString('es-AR')}`;
export const Offer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 2,
    minutes: 45,
    seconds: 12
  });
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => {
    // Diferimos el countdown para no competir con el LCP
    let timer: ReturnType<typeof setInterval> | undefined;
    const idle = (window as any).requestIdleCallback || ((cb: () => void) => setTimeout(cb, 400));
    const handle = idle(() => {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev.seconds > 0) return {
            ...prev,
            seconds: prev.seconds - 1
          };
          if (prev.minutes > 0) return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
          if (prev.hours > 0) return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
          return prev;
        });
      }, 1000);
    });
    return () => {
      if (timer) clearInterval(timer);
      if ((window as any).cancelIdleCallback && typeof handle === 'number') {
        ;
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);
  const toggle = (id: string) => {
    setSelected(prev => {
      const isAdding = !prev.includes(id);
      if (isAdding) {
        const bump = orderBumps.find(b => b.id === id);
        trackAddToCart({
          content_name: bump?.title,
          content_ids: [id],
          currency: 'ARS',
          value: bump?.price
        });
      }
      return prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    });
  };
  const addonsTotal = orderBumps.filter(b => selected.includes(b.id)).reduce((sum, b) => sum + b.price, 0);
  const total = BASE_PRICE + addonsTotal;
  return <Section id="oferta" className="bg-stone-900 text-white" data-id="element-165">
      <div className="max-w-4xl mx-auto bg-white text-stone-900 rounded-3xl overflow-hidden shadow-2xl border-4 border-brand-500 transform md:-translate-y-12" data-id="element-166">
        <div className="bg-brand-600 text-white text-center py-3 font-bold tracking-wide uppercase text-sm" data-id="element-167">
          Oferta Especial por Tiempo Limitado
        </div>

        <div className="p-6 sm:p-8 md:p-12" data-id="element-168">
          <div className="text-center mb-8" data-id="element-169">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-170">
              Llevate Plan Tiroides 360 hoy mismo
            </h2>
            <p className="text-stone-600 text-lg" data-id="element-171">
              Acceso inmediato y de por vida al material digital.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-10" data-id="element-172">
            <div className="text-center md:text-right" data-id="element-173">
              <p className="text-stone-400 font-medium mb-1" data-id="element-174">Precio Regular</p>
              <p className="text-2xl text-stone-400 line-through decoration-red-500 decoration-2" data-id="element-175">
                ARS $39.990
              </p>
            </div>

            <div className="hidden md:block w-px h-16 bg-stone-200" data-id="element-176"></div>

            <div className="text-center md:text-left" data-id="element-177">
              <p className="text-brand-600 font-bold mb-1 uppercase tracking-wider text-sm" data-id="element-178">
                Hoy con 57% OFF
              </p>
              <p className="text-5xl md:text-6xl font-black text-stone-900 tracking-tight" data-id="element-179">
                <span className="text-2xl align-top mr-1" data-id="element-180">ARS</span>$17.390
              </p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-red-700" data-id="element-181">
            <div className="flex items-center gap-2 font-bold" data-id="element-182">
              <TimerIcon className="w-5 h-5 animate-pulse" data-id="element-183" />
              <span data-id="element-184">La oferta termina en:</span>
            </div>
            <div className="flex gap-2 font-mono text-xl font-bold bg-white px-3 py-1 rounded shadow-sm" data-id="element-185">
              <span data-id="element-186">{String(timeLeft.hours).padStart(2, '0')}</span>:
              <span data-id="element-187">{String(timeLeft.minutes).padStart(2, '0')}</span>:
              <span data-id="element-188">{String(timeLeft.seconds).padStart(2, '0')}</span>
            </div>
          </div>

          {/* Quick reminder: bonuses incluidos */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6 flex items-center justify-center gap-2 text-center" data-id="element-189">
            <GiftIcon className="w-5 h-5 text-brand-700 shrink-0" data-id="element-190" />
            <p className="text-sm sm:text-base font-semibold text-brand-900" data-id="element-191">
              Incluye los 2 bonos de regalo (valor {formatARS(24980)}) —
              totalmente gratis
            </p>
          </div>

          {/* ORDER BUMP SECTION — restyled */}
          {orderBumps.map(bump => {
          const isSelected = selected.includes(bump.id);
          return <motion.div key={bump.id} whileHover={{
            y: -2
          }} animate={isSelected ? {
            scale: 1
          } : {
            scale: [1, 1.015, 1.03, 1.015, 1, 1, 1]
          }} transition={isSelected ? {
            type: 'spring',
            stiffness: 300
          } : {
            duration: 1.4,
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.15, 0.3, 0.45, 0.6, 0.8, 1]
          }} className={`relative mb-8 rounded-3xl border-[3px] overflow-hidden transition-colors duration-300 ${isSelected ? 'border-brand-600 shadow-2xl shadow-brand-600/30' : 'border-brand-800 shadow-xl'}`} data-id="element-192">
                {/* Floating "Solo por hoy" tag at top-right */}
                <motion.div animate={{
              scale: [1, 1.06, 1]
            }} transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-red-600 text-white text-xs sm:text-sm font-extrabold px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-xl shadow-red-600/40 flex items-center gap-1.5 uppercase tracking-wider z-20" data-id="element-193">
                  <SparklesIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-194" />
                  ¡Solo por hoy!
                </motion.div>

                {/* Inner card on cream background */}
                <button type="button" onClick={() => toggle(bump.id)} className="w-full text-left bg-gradient-to-b from-amber-50 to-amber-100/60 p-5 sm:p-6" data-id="element-195">
                  {/* Hero image */}
                  <div className="relative w-full mb-5 rounded-2xl overflow-hidden bg-white/40 shadow-md" data-id="element-196">
                    <motion.img src={`${bump.image}&width=800&quality=80&format=webp`} srcSet={`${bump.image}&width=480&quality=80&format=webp 480w, ${bump.image}&width=800&quality=80&format=webp 800w`} sizes="(max-width: 640px) 100vw, 800px" alt={bump.title} width={800} height={500} loading="lazy" decoding="async" className="w-full h-56 sm:h-72 object-contain" animate={isSelected ? {
                  scale: 1.02
                } : {
                  scale: [1, 1.015, 1]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-197" />
                  </div>

                  {/* Body: checkbox + content */}
                  <div className="flex items-start gap-4" data-id="element-198">
                    {/* Large attention-grabbing checkbox */}
                    <motion.div animate={!isSelected ? {
                  boxShadow: ['0 0 0 0 rgba(234,88,12,0)', '0 0 0 14px rgba(234,88,12,0.3)', '0 0 0 0 rgba(234,88,12,0)']
                } : {}} transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg border-[3px] flex items-center justify-center transition-colors ${isSelected ? 'bg-brand-600 border-brand-700' : 'bg-amber-300 border-accent-600 ring-2 ring-accent-500/40'}`} data-id="element-199">
                      {isSelected && <CheckIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" strokeWidth={3} data-id="element-200" />}
                    </motion.div>

                    {/* Text content */}
                    <div className="flex-1 min-w-0" data-id="element-201">
                      <p className="text-accent-600 font-extrabold uppercase tracking-wider text-xs sm:text-sm mb-2 flex items-center gap-1.5" data-id="element-202">
                        ¡Sí! Sumá a mi pedido <span aria-hidden data-id="element-203">→</span>
                      </p>
                      <h4 className="font-serif font-bold text-stone-900 text-xl sm:text-2xl leading-tight mb-2" data-id="element-204">
                        {bump.title}
                      </h4>
                      <p className="text-stone-700 font-bold text-sm sm:text-base mb-3 leading-snug" data-id="element-205">
                        Recetas libres de gluten y azúcar refinada — pensadas
                        para reducir la inflamación y cuidar tu tiroides.
                      </p>
                      <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-4" data-id="element-206">
                        Porque cuidar tu tiroides no significa resignar sabor.
                        Incluye <strong data-id="element-207">desayunos energizantes</strong>,{' '}
                        <strong data-id="element-208">almuerzos saciantes</strong>,{' '}
                        <strong data-id="element-209">cenas livianas</strong> y{' '}
                        <strong data-id="element-210">postres sin azúcar</strong> que toda tu familia
                        va a disfrutar.
                      </p>

                      {/* Divider */}
                      <div className="h-px bg-stone-300/70 my-4" data-id="element-211" />

                      {/* Price row */}
                      <div className="flex items-baseline gap-3" data-id="element-212">
                        <span className="text-stone-400 text-base sm:text-lg line-through font-medium" data-id="element-213">
                          {formatARS(bump.originalPrice)}
                        </span>
                        <span className="text-2xl sm:text-3xl font-black text-stone-900 tracking-tight" data-id="element-214">
                          + {formatARS(bump.price)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>;
        })}

          {/* DYNAMIC TOTAL */}
          {selected.length > 0 && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6 flex justify-between items-center" data-id="element-215">
              <span className="font-semibold text-brand-900" data-id="element-216">
                Total con tus extras:
              </span>
              <span className="text-2xl font-black text-brand-700" data-id="element-217">
                ARS {formatARS(total).replace('$', '$')}
              </span>
            </motion.div>}

          <div className="text-center mb-8" data-id="element-218">
            <p className="text-accent-600 font-bold flex items-center justify-center gap-2 mb-6" data-id="element-219">
              <span className="relative flex h-3 w-3" data-id="element-220">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75" data-id="element-221"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-accent-500" data-id="element-222"></span>
              </span>
              ¡Atención! Quedan solo 14 copias con este descuento
            </p>

            <button onClick={() => {
            const hasOrderBump = selected.length > 0;
            const checkoutUrl = hasOrderBump ? 'https://ebookpromax.online/cart/48810465165531:1' : 'https://ebookpromax.online/cart/48810464903387:1';
            trackInitiateCheckout({
              currency: 'ARS',
              value: total,
              content_ids: ['plan-tiroides-360', ...selected],
              num_items: 1 + selected.length
            });
            window.location.href = checkoutUrl;
          }} className="w-full md:w-auto px-10 py-5 text-xl font-bold rounded-xl text-white bg-brand-600 hover:bg-brand-700 transition-all shadow-[0_8px_30px_rgb(5,150,105,0.3)] hover:shadow-[0_8px_30px_rgb(5,150,105,0.5)] hover:-translate-y-1" data-id="element-223">
              Sí, quiero acceder ahora por{' '}
              <motion.span key={total} initial={{
              scale: 1.15,
              color: '#fde68a'
            }} animate={{
              scale: 1,
              color: '#ffffff'
            }} transition={{
              duration: 0.5
            }} className="inline-block" data-id="element-224">
                {formatARS(total)}
              </motion.span>
            </button>

            {/* MercadoLíder trust badge */}
            <div className="mt-6 flex justify-center" data-id="element-225">
              <img src="https://cdn.shopify.com/s/files/1/0829/1431/2411/files/MERCADOLIDER.jpg?v=1779799747&width=480&quality=80&format=webp" alt="MercadoLíder - Vendedor verificado" width={480} height={150} loading="lazy" decoding="async" className="w-full max-w-md h-auto object-contain rounded-lg" data-id="element-226" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 border-t border-stone-100 pt-8 text-center text-sm text-stone-500 font-medium" data-id="element-227">
            <div className="flex flex-col items-center gap-2" data-id="element-228">
              <CreditCardIcon className="w-6 h-6 text-stone-400" data-id="element-229" />
              <span data-id="element-230">Pago Seguro</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-231">
              <MailIcon className="w-6 h-6 text-stone-400" data-id="element-232" />
              <span data-id="element-233">Envío Inmediato</span>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-234">
              <SmartphoneIcon className="w-6 h-6 text-stone-400" data-id="element-235" />
              <span data-id="element-236">Acceso de por vida</span>
            </div>
          </div>
        </div>
      </div>
    </Section>;
};